import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  Chip,
  TextField,
  Checkbox,
  Paper,
  FormControlLabel,
  FormGroup,
  Button,
} from '@mui/material';
import { ClientData } from '../../types';

export interface ProjectSettingsFieldsProps {
  field: keyof ClientData;
  title: string;
  description: string;
  options: string[];
  clientData: ClientData;
  customOptions: ClientData;
  selectedOptionsInputs: { [key: string]: string };
  onCheckboxChange: (
    field: keyof ClientData,
    value: string
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  onCustomOptionAdd: (field: keyof ClientData) => void;
  onCustomOptionRemove: (field: keyof ClientData, item: string) => void;
  onOptionInputChange: (field: keyof ClientData, value: string) => void;
  // Step-specific "coming soon" flag (e.g. notificationFormat today) — kept
  // separate from `canManage` because the dialog does not gate the custom
  // option Chip's delete affordance on it, only on `canManage`.
  disabled?: boolean;
  canManage: boolean;
  // Opt-in two-column checkbox layout (COM-637 Task 3: the onboarding
  // wizard's Figma frames lay out long option lists — Topics of Interest,
  // Document Types — in two columns). Defaults to false/undefined so the
  // settings page, which never passes this prop, keeps its exact
  // single-column FormGroup output byte-identical.
  twoColumn?: boolean;
}

// Renders exactly one settings block (checkbox list + custom-options input)
// for a single ClientData field. Presentational only — no context, no
// socket, no data fetching. Shared by ClientDataDialog (onboarding) and the
// settings page (renders one of these per field).
const ProjectSettingsFields: React.FC<ProjectSettingsFieldsProps> = ({
  field,
  title,
  description,
  options,
  clientData,
  customOptions,
  selectedOptionsInputs,
  onCheckboxChange,
  onCustomOptionAdd,
  onCustomOptionRemove,
  onOptionInputChange,
  disabled,
  canManage,
  twoColumn,
}) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        {/* pl offsets MUI's own -11px FormControlLabel margin, which otherwise
            pulls the checkboxes flush against (and visually past) the left
            edge of the block. Applies to both surfaces that render these
            fields — the settings page and the onboarding wizard. */}
        <FormGroup sx={{ pl: '11px', ...(twoColumn ? { columnCount: 2, columnGap: 4 } : {}) }}>
          {options.map((option) => (
            <FormControlLabel
              key={option}
              sx={twoColumn ? { display: 'flex', breakInside: 'avoid' } : undefined}
              control={
                <Checkbox
                  checked={clientData[field].includes(option)}
                  onChange={onCheckboxChange(field, option)}
                  disabled={disabled || !canManage}
                />
              }
              label={option}
            />
          ))}
        </FormGroup>
      </FormControl>

      {/* Always show Selected options section */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Custom options:
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          {customOptions[field].length > 0 ? (
            customOptions[field].map((item) => (
              <Chip
                key={item}
                label={item}
                onDelete={canManage ? () => onCustomOptionRemove(field, item) : undefined}
                color="primary"
                variant="outlined"
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No custom options added yet
            </Typography>
          )}
        </Box>

        {/* Custom input for selected options */}
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            size="small"
            value={selectedOptionsInputs[field] || ''}
            onChange={(e) => onOptionInputChange(field, e.target.value)}
            placeholder="Add custom option"
            sx={{ flexGrow: 1 }}
            disabled={disabled || !canManage}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                onCustomOptionAdd(field);
              }
            }}
          />
          <Button
            variant="outlined"
            onClick={() => onCustomOptionAdd(field)}
            disabled={!selectedOptionsInputs[field]?.trim() || disabled || !canManage}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            Add
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ProjectSettingsFields;
